import { getCountryName, getFormattedCountryName } from "~/lib/helpers/constants";
import { getCurrentCustomerCount } from "~/lib/helpers/StringHelper";
import { workspaceSlug } from "~/lib/helpers/slugHelper";
import {
  brands,
  getCountrySpecificMeta,
  HOME_PAGE_SECTION_IDS,
} from "./constants";

type BuildHomeChatPageContextArgs = {
  region: string;
  countryName?: string;
  pathname?: string | null;
  categories?: Array<{ option?: string; name?: string; value?: string }>;
  initialUserCurrency?: string | null;
  partners?: any[];
  nearByProperties?: any[];
  featuredPlaces?: any;
  blogs?: any;
  homeSummary?: any;
};

function mapListingForChat(p: any) {
  const building = p?.buildingName || p?.name || p?.address || "";
  const address =
    p?.streetNumber || p?.streetName || p?.city
      ? `${[!p?.hideStreetNumber && p?.streetNumber ? `${p.streetNumber} ` : "", p?.streetName].filter(Boolean).join("")}${p?.city ? `, ${p.city}` : ""}`.trim()
      : p?.addressFormula || "";
  const officeType =
    p?.propertyType__c === "Serviced Office (Classic)" ||
    p?.propertyType__c === "Serviced Office (Creative)"
      ? "Serviced Office"
      : p?.propertyType__c || p?.spaceTypeFormula || null;

  const amount =
    typeof p?.avgMonthlyCostPerWorkstation === "number"
      ? p.avgMonthlyCostPerWorkstation
      : null;
  const otherCount =
    typeof p?.propertyTotalActiveListings === "number"
      ? p.propertyTotalActiveListings
      : null;
  const minDesks = typeof p?.minDesks === "number" ? p.minDesks : null;
  const maxDesks =
    typeof p?.maxDesks === "number"
      ? p.maxDesks
      : typeof p?.noOfWS === "number"
        ? p.noOfWS
        : null;

  return {
    id: p?.id || p?.propertyId || p?.sfid || null,
    property_id: p?.propertyId || null,
    building: building || null,
    address: address || null,
    city: p?.city || null,
    office_type: officeType,
    capacity: { min_desks: minDesks, max_desks: maxDesks },
    price: amount != null ? { amount, unit: "per_desk", period: "month" } : null,
    other_options:
      otherCount != null
        ? {
            count: otherCount,
            range:
              minDesks != null && maxDesks != null
                ? `${minDesks}-${maxDesks} desks`
                : null,
          }
        : null,
  };
}

function mapPartnerForChat(p: any) {
  return {
    name: p?.name || null,
    slug:
      p?.name && p?.sfid
        ? workspaceSlug({ name: p.name, sfid: p.sfid, id: p?.id })
        : null,
    sfid: p?.sfid || null,
  };
}

function mapPlaceForChat(place: any) {
  return {
    name: place?.name || place?.city || place?.displayName || null,
    city: place?.city || null,
    state: place?.state || null,
    country: place?.country || null,
    slug: place?.slug || place?.url || null,
    image: place?.image || place?.imageUrl || null,
  };
}

function mapBlogForChat(blog: any) {
  return {
    title: blog?.title || blog?.name || null,
    slug: blog?.slug || blog?.url || null,
    category: blog?.blogCategory || blog?.category || null,
  };
}

const OFFICE_OPTIONS = [
  {
    title: "Serviced Office",
    description:
      "Move into a ready-to-use serviced office with all-inclusive amenities and support.",
    link: "serviced-offices",
  },
  {
    title: "Coworking Space",
    description:
      "Work alongside inspiring teams in flexible, shared coworking spaces built for connection.",
    link: "coworking-space",
  },
  {
    title: "Private Office",
    description:
      "Secure a fully furnished private office designed for focus, privacy, and productivity.",
    link: "private-offices",
  },
  {
    title: "Shared Office",
    description:
      "Enjoy cost-effective shared office spaces where teams collaborate and grow together.",
    link: "shared-offices",
  },
  {
    title: "Enterprise Office",
    description: "Custom HQ solutions for large, growing teams.",
    link: "enterprise-office-space",
  },
  {
    title: "Sublease Office",
    description:
      "Take over flexible sublet offices and enjoy full control without the long-term lease.",
    link: "conventional-sublet-offices",
  },
];

const WORKSPACE_FINDER_FEATURES = [
  {
    title: "Best Deal Guarantee",
    description:
      "From a desk, office or an entire floor, we guarantee to get you the best deal.",
  },
  {
    title: "Dedicated account managers",
    description:
      "Our team is real, not bots, and here to help you find your perfect workspace.",
  },
  {
    title: "100% free of charge",
    description: "Search, tour, and get expert advice with no cost or obligation.",
  },
  {
    title: "Biggest Range of Workspaces",
    description:
      "An extensive range of coworking and serviced offices around the world.",
  },
];

/** Builds structured home-page context for the Chat Assistant `pageBody`. */
export async function buildHomeChatPageContext({
  region,
  countryName,
  pathname,
  categories = [],
  initialUserCurrency,
  partners: partnersInput,
  nearByProperties: nearByInput,
  featuredPlaces: featuredPlacesInput,
  blogs: blogsInput,
  homeSummary: homeSummaryInput,
}: BuildHomeChatPageContextArgs) {
  const formattedCountry = getFormattedCountryName(region);
  const country = getCountryName(region);

  const [partnersRes, featuredRes, blogsRes, placesRes, summaryRes] =
    await Promise.allSettled([
      partnersInput,
      nearByInput,
      blogsInput,
      featuredPlacesInput,
      homeSummaryInput,
    ]);

  const partners =
    partnersRes.status === "fulfilled" && Array.isArray(partnersRes.value)
      ? partnersRes.value
      : [];
  const featured =
    featuredRes.status === "fulfilled" && Array.isArray(featuredRes.value)
      ? featuredRes.value
      : [];
  const blogs =
    blogsRes.status === "fulfilled" ? (blogsRes.value as any) : null;
  const featuredPlacesRaw =
    placesRes.status === "fulfilled" ? placesRes.value : null;
  const featuredPlacesList = Array.isArray(featuredPlacesRaw?.results)
    ? featuredPlacesRaw.results
    : Array.isArray(featuredPlacesRaw)
      ? featuredPlacesRaw
      : [];
  const homeSummary =
    summaryRes.status === "fulfilled" ? summaryRes.value : null;

  const officeTypeCategories = Array.isArray(categories)
    ? categories
        .map((c) => ({ name: String(c?.option || c?.name || "").trim() }))
        .filter((c) => c.name)
    : [];

  const featuredListings = featured.slice(0, 8).map(mapListingForChat);
  const featuredPartners = partners.slice(0, 8).map(mapPartnerForChat);
  const popularPlaces = featuredPlacesList.slice(0, 8).map(mapPlaceForChat);
  const blogItems = Array.isArray(blogs?.blogs)
    ? blogs.blogs.slice(0, 6).map(mapBlogForChat).filter((b: any) => b.title)
    : [];

  const currency =
    featured?.[0]?.currencyIsoCode || initialUserCurrency || null;
  const aboutMeta = getCountrySpecificMeta(region);
  const aboutUs =
    aboutMeta?.aboutUs ||
    `At Office Hub ${countryName || country}, we connect professionals and businesses with flexible office spaces across ${countryName || country}.`;

  const whyFeatures = [
    {
      title: "Transparent and Verified Listings",
      description: `Every workspace in ${formattedCountry} is checked, verified, and updated with accurate pricing.`,
    },
    {
      title: "Local Experts, Real Support",
      description: `Our ${formattedCountry}-based workspace specialists will shortlist the best options, arrange tours, and negotiate great deals on your behalf.`,
    },
    {
      title: "All-in-One Marketplace",
      description: `Explore coworking, serviced, private, and enterprise offices across ${formattedCountry} — all in one place.`,
    },
    {
      title: "Free and Hassle-Free",
      description:
        "Our help is 100% free. No hidden fees, no pressure, just honest advice and local expertise to find your ideal workspace.",
    },
  ];

  const page = {
    [HOME_PAGE_SECTION_IDS.hero]: {
      section_id: HOME_PAGE_SECTION_IDS.hero,
      headline: `Find & Rent Coworking, Serviced and Shared Offices in ${formattedCountry}`,
      office_type_categories: officeTypeCategories,
      total_properties: homeSummary?.totalProperties ?? null,
      happy_clients: getCurrentCustomerCount(),
      google_rating:
        homeSummary?.sumRatings != null
          ? Number(parseFloat(homeSummary.sumRatings).toFixed(1))
          : 5.0,
    },
    [HOME_PAGE_SECTION_IDS.workspaceFinder]: {
      section_id: HOME_PAGE_SECTION_IDS.workspaceFinder,
      headline: `Why Businesses Across ${formattedCountry} Choose Office Hub`,
      features: WORKSPACE_FINDER_FEATURES,
      client_brands: brands,
    },
    [HOME_PAGE_SECTION_IDS.topLocations]: {
      section_id: HOME_PAGE_SECTION_IDS.topLocations,
      heading: `Top Office Locations in ${formattedCountry}`,
      subheading: `Explore coworking, serviced and shared offices in ${formattedCountry}'s key business hubs.`,
      listings: featuredListings,
    },
    [HOME_PAGE_SECTION_IDS.officeOptions]: {
      section_id: HOME_PAGE_SECTION_IDS.officeOptions,
      heading: "Discover the Right Workspace for Your Business",
      subheading: `Explore workspaces across ${formattedCountry} that match your goals, from social coworking hubs to private offices and enterprise HQs.`,
      options: OFFICE_OPTIONS,
    },
    [HOME_PAGE_SECTION_IDS.whyOfficeHub]: {
      section_id: HOME_PAGE_SECTION_IDS.whyOfficeHub,
      heading: "Why Office Hub",
      features: whyFeatures,
    },
    [HOME_PAGE_SECTION_IDS.popularLocations]: {
      section_id: HOME_PAGE_SECTION_IDS.popularLocations,
      heading: "Popular Office Locations Near You",
      places: popularPlaces,
    },
    [HOME_PAGE_SECTION_IDS.partners]: {
      section_id: HOME_PAGE_SECTION_IDS.partners,
      heading: "Explore Offices from Our Premium Partners",
      description: "Access exclusive workspaces from top global partners.",
      partners: featuredPartners,
    },
    [HOME_PAGE_SECTION_IDS.listOnOfficeHub]: {
      section_id: HOME_PAGE_SECTION_IDS.listOnOfficeHub,
      heading: "List on Office Hub",
      description:
        "Explore nearby workspaces on the map and discover flexible office options in your area.",
    },
    [HOME_PAGE_SECTION_IDS.about]: {
      section_id: HOME_PAGE_SECTION_IDS.about,
      heading: "About Office Hub",
      subheading:
        "Office Hub is your ultimate partner in revolutionizing the way you work.",
      about_us: aboutUs.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, ""),
      founder_message:
        "At Office Hub, our mission is simple — to make finding and managing workspaces effortless. We're here to connect people, places, and possibilities through innovation, transparency, and trust.",
    },
    [HOME_PAGE_SECTION_IDS.googleReviews]: {
      section_id: HOME_PAGE_SECTION_IDS.googleReviews,
      heading: "What Our Clients Say About Office Hub",
      subheading: `Since 2014, we've helped over ${getCurrentCustomerCount()}+ businesses find their ideal workspace.`,
      google_rating:
        homeSummary?.sumRatings != null
          ? Number(parseFloat(homeSummary.sumRatings).toFixed(1))
          : null,
    },
    [HOME_PAGE_SECTION_IDS.guidesAndTips]: {
      section_id: HOME_PAGE_SECTION_IDS.guidesAndTips,
      heading: "Explore Workspace Tips and Insights",
      subheading:
        "Discover expert guides, office trends, and workspace ideas to help your business grow smarter.",
      blogs:
        blogItems.length > 0
          ? blogItems
          : [{ title: "Explore Workspace Tips and Insights" }],
    },
    [HOME_PAGE_SECTION_IDS.officeSearchBanner]: {
      section_id: HOME_PAGE_SECTION_IDS.officeSearchBanner,
      heading: `We are ${formattedCountry}'s No. 1 marketplace for serviced, coworking, and shared office space.`,
      description: `Connect with our local experts for flexible workspace solutions across ${formattedCountry}. Get personalized recommendations, arrange tours, and secure the best deals — all at no cost.`,
    },
  };

  return {
    source: "structured",
    page_type: "home",
    url: pathname || null,
    country,
    currency,
    location: null,
    total_results: null,
    page,
  };
}
